import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Pencil,
  Pin,
  Plus,
  Scale,
  Search,
  Trash2,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { TagBadge } from "@/components/ui/TagBadge";
import { VoteButtons } from "@/components/ui/VoteButtons";
import { useViewMode } from "@/hooks/useViewMode";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";

const DECISION_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "category", label: "Category", defaultVisible: false },
  { key: "impact", label: "Impact" },
  { key: "status", label: "Status" },
  { key: "votes", label: "Votes" },
  { key: "decidedBy", label: "Decided By" },
  { key: "date", label: "Date" },
  { key: "context", label: "Context" },
];
import {
  useDecisionCategories,
  useDecisionImpacts,
  useDecisionList,
  useDecisionStatuses,
  useDecisionTags,
  useDeleteDecision,
  useRemoveDecisionVote,
  useToggleDecisionPin,
  useVoteDecision,
} from "../hooks/useDecisions";
import { NewDecisionDrawer } from "../components/NewDecisionDrawer";
import { DecisionCard } from "../components/DecisionCard";
import type { DecisionListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  open: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  decided: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  implemented: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  archived: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const IMPACT_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: "hsl(0,80%,95%)", text: "hsl(0,75%,38%)" },
  high: { bg: "hsl(25,95%,94%)", text: "hsl(25,85%,38%)" },
  medium: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)" },
  low: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function DecisionListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [impactFilter, setImpactFilter] = useState("");
  const [meetingFilter, setMeetingFilter] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const { mode: viewMode, toggle: toggleViewMode } = useViewMode(
    "decisions",
    "list",
  );
  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    minReached,
  } = useColumnVisibility("decisions", DECISION_COLUMNS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const statuses = useDecisionStatuses().data ?? [];
  const categories = useDecisionCategories().data ?? [];
  const impacts = useDecisionImpacts().data ?? [];
  const tagOptions = useDecisionTags().data ?? [];

  const tagLabels = useMemo(
    () => Object.fromEntries(tagOptions.map((t) => [t.value, t.label])),
    [tagOptions],
  );
  const statusLabels = useMemo(
    () => Object.fromEntries(statuses.map((s) => [s.value, s.label])),
    [statuses],
  );
  const categoryLabels = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.value, c.label])),
    [categories],
  );
  const impactLabels = useMemo(
    () => Object.fromEntries(impacts.map((i) => [i.value, i.label])),
    [impacts],
  );

  const { data, isLoading, isError, error } = useDecisionList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    impact: impactFilter || undefined,
    meetingId: meetingFilter || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
  });
  const { data: meetingsResult } = useMeetingList({ page: 1, limit: 100 });
  const meetings = meetingsResult?.data ?? [];

  const togglePin = useToggleDecisionPin();
  const deleteDecision = useDeleteDecision();

  const allDecisions: DecisionListItem[] = data?.data ?? [];
  const pinned = useMemo(
    () => allDecisions.filter((d) => d.isPinned),
    [allDecisions],
  );
  const unpinned = useMemo(
    () => allDecisions.filter((d) => !d.isPinned),
    [allDecisions],
  );
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  async function handleTogglePin(d: DecisionListItem) {
    await togglePin.mutateAsync({ id: d.id, isPinned: !d.isPinned });
    showToast(d.isPinned ? "Decision unpinned" : "Decision pinned");
  }

  function handleDelete(e: React.MouseEvent, d: DecisionListItem) {
    e.stopPropagation();
    setDeleteTarget({ id: d.id, name: d.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteDecision.mutateAsync(deleteTarget.id);
    showToast("Decision deleted");
  }

  function openDecision(id: string) {
    navigate(`/decisions/${id}`);
  }

  function openEdit(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    navigate(`/decisions/${id}?edit=true`);
  }

  function toggleTag(value: string) {
    setPage(1);
    setSelectedTags((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value],
    );
  }

  function VoteCell({ d }: { d: DecisionListItem }) {
    const voteMut = useVoteDecision(d.id);
    const removeMut = useRemoveDecisionVote(d.id);
    async function handleVote(type: "up" | "down") {
      if (d.currentUserVote === type) {
        await removeMut.mutateAsync();
      } else {
        await voteMut.mutateAsync(type);
      }
    }
    return (
      <VoteButtons
        votesUp={d.votesUp}
        votesDown={d.votesDown}
        userVote={d.currentUserVote}
        onVote={handleVote}
        size="sm"
      />
    );
  }

  function renderTable(decisions: DecisionListItem[]) {
    return (
      <table
        className="w-full text-[13px]"
        style={{ borderCollapse: "collapse" }}
      >
        <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
              #
            </th>
            <th className="px-[14px] py-[9px]">Title & Tags</th>
            <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
              Category
            </th>
            <th className="px-[14px] py-[9px]" style={{ width: 100 }}>
              Impact
            </th>
            <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
              Status
            </th>
            <th className="px-[14px] py-[9px]" style={{ width: 150 }}>
              Votes
            </th>
            <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
              Decided By
            </th>
            <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
              Decided At
            </th>
            <th className="px-[14px] py-[9px]">Context</th>
            <th
              className="px-[14px] py-[9px] text-right"
              style={{ width: 140 }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {decisions.map((d) => {
            const chain = [d.companyName, d.productName, d.projectName]
              .filter(Boolean)
              .join(" › ");
            const statusColor = STATUS_COLORS[d.status] ?? STATUS_COLORS.open;
            const impactColor =
              IMPACT_COLORS[d.impact] ?? IMPACT_COLORS.medium;
            return (
              <tr
                key={d.id}
                className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                onClick={() => openDecision(d.id)}
              >
                <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                  {d.code ?? "—"}
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center gap-1.5">
                    {d.isPinned && (
                      <Pin
                        className="h-3 w-3"
                        strokeWidth={1.5}
                        style={{
                          color: "hsl(40,95%,50%)",
                          fill: "hsl(40,95%,55%)",
                        }}
                      />
                    )}
                    <span className="font-medium text-foreground">
                      {d.title}
                    </span>
                  </div>
                  {d.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {d.tags.slice(0, 4).map((t) => (
                        <TagBadge
                          key={t}
                          value={t}
                          label={tagLabels[t] ?? t}
                          size="sm"
                        />
                      ))}
                      {d.tags.length > 4 && (
                        <span className="text-[10px] text-muted-foreground">
                          +{d.tags.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-[14px] py-[11px] text-muted-foreground">
                  {d.category
                    ? (categoryLabels[d.category] ?? d.category)
                    : "—"}
                </td>
                <td className="px-[14px] py-[11px]">
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
                    style={{
                      background: impactColor.bg,
                      color: impactColor.text,
                    }}
                  >
                    {impactLabels[d.impact] ?? d.impact}
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
                    style={{
                      background: statusColor.bg,
                      color: statusColor.text,
                    }}
                  >
                    {statusLabels[d.status] ?? d.status}
                  </span>
                </td>
                <td
                  className="px-[14px] py-[11px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <VoteCell d={d} />
                </td>
                <td className="px-[14px] py-[11px] text-foreground">
                  {d.decidedByName ?? "—"}
                </td>
                <td className="px-[14px] py-[11px] text-muted-foreground">
                  {formatDate(d.decidedAt)}
                </td>
                <td className="px-[14px] py-[11px] text-muted-foreground">
                  <div className="text-[12px]">{chain || "—"}</div>
                </td>
                <td
                  className="px-[14px] py-[11px] text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => openDecision(d.id)}
                      className={iconBtnBase}
                      title="View"
                    >
                      <Eye className="h-[15px] w-[15px]" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => openEdit(e, d.id)}
                      className={iconBtnBase}
                      title="Edit"
                    >
                      <Pencil
                        className="h-[15px] w-[15px]"
                        strokeWidth={1.5}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTogglePin(d)}
                      className={iconBtnBase}
                      title={d.isPinned ? "Unpin" : "Pin"}
                    >
                      <Pin
                        className="h-[15px] w-[15px]"
                        strokeWidth={1.5}
                        style={{
                          color: d.isPinned ? "hsl(40,95%,50%)" : undefined,
                          fill: d.isPinned ? "hsl(40,95%,55%)" : "none",
                        }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, d)}
                      className={iconBtnDelete}
                      title="Delete"
                    >
                      <Trash2
                        className="h-[15px] w-[15px]"
                        strokeWidth={1.5}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function GridDecision({ d }: { d: DecisionListItem }) {
    const voteMut = useVoteDecision(d.id);
    const removeMut = useRemoveDecisionVote(d.id);
    async function handleVote(type: "up" | "down") {
      if (d.currentUserVote === type) {
        await removeMut.mutateAsync();
      } else {
        await voteMut.mutateAsync(type);
      }
    }
    return (
      <DecisionCard
        decision={d}
        onClick={() => openDecision(d.id)}
        onTogglePin={() => handleTogglePin(d)}
        onVote={handleVote}
        tagLabels={tagLabels}
        categoryLabels={categoryLabels}
      />
    );
  }

  function renderGrid(decisions: DecisionListItem[]) {
    return (
      <div
        className="grid gap-3 p-3"
        style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
      >
        {decisions.map((d) => (
          <GridDecision key={d.id} d={d} />
        ))}
      </div>
    );
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Planning" },
        { label: "Decisions" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scale
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Decisions</h1>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggleButton mode={viewMode} onToggle={toggleViewMode} />
            <ExportButton
              module="decisions"
              filters={{
                search: search || undefined,
                status: statusFilter || undefined,
                category: categoryFilter || undefined,
                impact: impactFilter || undefined,
                meetingId: meetingFilter || undefined,
              }}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={DECISION_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Decision
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load decisions</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <div className="space-y-2">
                <div
                  className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide"
                  style={{ color: "hsl(40,95%,35%)" }}
                >
                  <Pin
                    className="h-3 w-3"
                    strokeWidth={1.5}
                    style={{
                      color: "hsl(40,95%,50%)",
                      fill: "hsl(40,95%,55%)",
                    }}
                  />
                  Pinned
                </div>
                <div
                  className="flex gap-3 overflow-x-auto pb-2"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {pinned.map((d) => (
                    <div key={d.id} style={{ minWidth: 260, maxWidth: 260 }}>
                      <GridDecision d={d} />
                    </div>
                  ))}
                </div>
                <div className="border-b" style={{ borderColor: BORDER }} />
              </div>
            )}

            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search decisions..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                {statuses.length === 0 ? (
                  <>
                    <option value="open">Open</option>
                    <option value="decided">Decided</option>
                    <option value="implemented">Implemented</option>
                    <option value="archived">Archived</option>
                  </>
                ) : (
                  statuses.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.label}
                    </option>
                  ))
                )}
              </Select>
              <Select
                value={categoryFilter}
                onChange={(e) => resetPage(setCategoryFilter)(e.target.value)}
                className="w-44 shrink-0"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <Select
                value={impactFilter}
                onChange={(e) => resetPage(setImpactFilter)(e.target.value)}
                className="w-36 shrink-0"
              >
                <option value="">All Impact</option>
                {impacts.length === 0 ? (
                  <>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </>
                ) : (
                  impacts.map((i) => (
                    <option key={i.id} value={i.value}>
                      {i.label}
                    </option>
                  ))
                )}
              </Select>
              <Select
                value={meetingFilter}
                onChange={(e) => resetPage(setMeetingFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Meetings</option>
                {meetings.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.title}
                  </option>
                ))}
              </Select>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setTagPickerOpen((v) => !v)}
                  className="inline-flex h-9 items-center gap-2 rounded-md border bg-white px-3 text-sm"
                  style={{ borderColor: BORDER }}
                >
                  Tags
                  {selectedTags.length > 0 && (
                    <span
                      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
                      style={{
                        background: "hsl(210,55%,88%)",
                        color: "hsl(210,100%,25%)",
                      }}
                    >
                      {selectedTags.length}
                    </span>
                  )}
                  <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
                </button>
                {tagPickerOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[9990]"
                      onClick={() => setTagPickerOpen(false)}
                    />
                    <div
                      className="absolute z-[9991] mt-1 w-[280px] rounded-md border bg-white p-3 shadow-lg"
                      style={{ borderColor: BORDER }}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase text-muted-foreground">
                          Filter by tags
                        </span>
                        {selectedTags.length > 0 && (
                          <button
                            type="button"
                            className="text-[11px] text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setSelectedTags([]);
                              setPage(1);
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {tagOptions.map((tag) => {
                          const selected = selectedTags.includes(tag.value);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => toggleTag(tag.value)}
                              style={{ opacity: selected ? 1 : 0.5 }}
                            >
                              <TagBadge
                                value={tag.value}
                                label={tag.label}
                                size="sm"
                              />
                            </button>
                          );
                        })}
                        {tagOptions.length === 0 && (
                          <span className="text-xs text-muted-foreground">
                            No tags configured.
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

            </div>

            {selectedTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">
                  Filtering:
                </span>
                {selectedTags.map((t) => (
                  <TagBadge
                    key={t}
                    value={t}
                    label={tagLabels[t] ?? t}
                    size="sm"
                    onRemove={() => toggleTag(t)}
                  />
                ))}
              </div>
            )}

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading decisions…
                </div>
              ) : allDecisions.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No decisions match the current filters.
                </div>
              ) : viewMode === "list" ? (
                renderTable(unpinned.length > 0 ? unpinned : allDecisions)
              ) : (
                renderGrid(unpinned.length > 0 ? unpinned : allDecisions)
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewDecisionDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Decision created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete decision?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Decisions list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
